import { SettingOutlined } from '@ant-design/icons';
import { Button, Flex } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import ColorSchemeSwitcher from '@/features/profile/ColorSchemeSwitcher';
import { getProfileMenuItems } from '@/features/profile/profileMenuItems';
import TimezoneSwitcher from '@/features/profile/TimezoneSwitcher';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import useHirePortContext from '@/hooks/useHirePortContext';
import { useUrlState } from '@/hooks/useUrlState';
import { useAppDispatch } from '@/store/hooks';
import { logoutUser } from '@/store/userSlice';
import { getAvatarPalette } from '@/theme/avatarPalette';
import Avatar from '@/UI/Avatar/Avatar';
import ProfileMenuPopupView from '@/UI/Popups/ProfileMenuPopup';

import type { MenuProps } from 'antd';

/**
 * Connected profile menu for the app nav. Builds menu items here so
 * `@/UI/Popups/ProfileMenuPopup` stays presentational.
 */
const ProfileMenuPopup = () => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const { history } = useUrlState();
  const { user, isAdmin, isRecruiter } = useCurrentUser();
  const { colorScheme } = useHirePortContext();
  const profileAvatarColor = getAvatarPalette(colorScheme)[7];

  const handleLogout = useCallback(() => {
    void dispatch(logoutUser());
    const confirmLinkedin = localStorage.getItem('hp_linkedin_login');
    if (confirmLinkedin === 'true') {
      localStorage.removeItem('hp_linkedin_login');
      window.open('https://linkedin.com/m/logout');
    }
  }, [dispatch]);

  const handleMenuClick = useCallback<NonNullable<MenuProps['onClick']>>(
    ({ key }) => {
      history.push(key);
    },
    [history],
  );

  if (!user) return null;

  const triggerImage = isRecruiter ? user.image : user.company?.image;
  const profileAriaLabel = t('PROFILE_SETTINGS');

  const menuItems: NonNullable<MenuProps['items']> = getProfileMenuItems(
    user,
  ).map((menuItem) => ({
    key: menuItem.route,
    icon: menuItem.icon,
    label: t(menuItem.titleKey),
  }));

  return (
    <ProfileMenuPopupView
      menuItems={menuItems}
      onMenuClick={handleMenuClick}
      onLogout={handleLogout}
      extra={
        <Flex vertical gap="small">
          <TimezoneSwitcher />
          <ColorSchemeSwitcher />
        </Flex>
      }
      renderTrigger={(open) =>
        isAdmin ? (
          <Button
            size="large"
            variant={open ? 'filled' : 'link'}
            color={open ? 'primary' : 'default'}
            shape="circle"
            aria-label={profileAriaLabel}
            data-testid="profile-menu-trigger"
            icon={<SettingOutlined />}
          />
        ) : (
          <Button
            type="text"
            aria-label={profileAriaLabel}
            data-testid="profile-menu-trigger"
            shape="circle"
          >
            <Avatar
              name={user.name || ''}
              color={profileAvatarColor}
              src={triggerImage}
            />
          </Button>
        )
      }
    />
  );
};

export default ProfileMenuPopup;
